import React, {
  useLayoutEffect, useMemo, useRef, useState,
} from "react";
import styled, { keyframes, useTheme } from "styled-components";
import { tokens } from "../../activity-logs/styles";
import { IOS } from "../../activity-logs/labels";
import { formatCompact, formatMonth } from "../../creditos/dashboardData";
import { tokens as T } from "../../../styles/designTokens";

/**
 * Faturamento × compras por mês, com o tributo líquido na faixa de baixo, no mesmo eixo.
 * A faixa entre as linhas muda de cor quando as compras passam do faturamento.
 * Clicar seleciona o mês, e a aba inteira acompanha; setas navegam e Esc volta ao período.
 */

const REVENUE = IOS.blue;
const PURCHASES = IOS.green;
const PAY = IOS.red;
const CREDIT = IOS.green;
const PAD = { top: 16, right: 92, bottom: 26, left: 56 };
const LANE = 62;
const LANE_GAP = 30;

const draw = keyframes`from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; }`;
const fade = keyframes`from { opacity: 0; } to { opacity: 1; }`;

const Stage = styled.div`
  position: relative;
  flex: 1;
  min-height: 356px;
  margin-top: 14px;
  user-select: none;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
  border-radius: 10px;

  &:focus-visible { outline: none; box-shadow: 0 0 0 3px ${T.brandRing}; }

  svg { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }
  .curve { fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; transition: d 260ms cubic-bezier(0.2, 0.8, 0.2, 1); }
  .curve.enter { stroke-dasharray: 1; animation: ${draw} 720ms cubic-bezier(0.3, 0.7, 0.2, 1) both; }
  .band { animation: ${fade} 600ms ease both; }
  .bar { transition: opacity 160ms ease; }

  @media (prefers-reduced-motion: reduce) {
    .curve { transition: none; }
    .curve.enter, .band { animation: none; }
  }
`;

const Tick = styled.span`
  position: absolute;
  font-size: 10.5px;
  color: ${({ theme }) => tokens.label3(theme)};
  pointer-events: none;
  white-space: nowrap;
  &.on { color: ${({ theme }) => theme.text}; font-weight: 600; }
`;

const EndLabel = styled.span`
  position: absolute;
  transform: translateY(-50%);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: -0.01em;
  white-space: nowrap;
  pointer-events: none;
`;

const Chip = styled.span`
  position: absolute;
  padding: 2px 7px;
  border-radius: 7px;
  font-size: 11.5px;
  font-weight: 650;
  letter-spacing: -0.02em;
  white-space: nowrap;
  pointer-events: none;
  color: ${({ $color }) => $color};
  background: ${({ theme }) => tokens.solid(theme)};
  box-shadow: 0 0 0 0.5px ${({ $color }) => `${$color}66`}, 0 4px 12px rgba(0, 0, 0, 0.28);
`;

const Glass = styled.div`
  position: absolute;
  top: 8px;
  min-width: 172px;
  padding: 8px 11px;
  border-radius: 12px;
  font-size: 11.5px;
  color: ${({ theme }) => theme.text};
  background: ${({ theme }) => (tokens.dk(theme) ? "rgba(44, 44, 48, 0.82)" : "rgba(255, 255, 255, 0.82)")};
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.3), inset 0 0 0 0.5px rgba(255, 255, 255, 0.12);
  pointer-events: none;
  z-index: 2;

  .p { font-weight: 650; letter-spacing: -0.02em; }
  .r { display: flex; justify-content: space-between; gap: 14px; margin-top: 3px; }
  .r span { display: inline-flex; align-items: center; gap: 5px; color: ${({ theme }) => tokens.label2(theme)}; }
  .r i { width: 6px; height: 6px; border-radius: 50%; }
  .r b { font-weight: 600; }
`;

const Hint = styled.div`
  position: absolute;
  left: ${PAD.left + 8}px;
  top: 0;
  font-size: 11px;
  font-weight: 300;
  color: ${({ theme }) => tokens.label2(theme)};
  pointer-events: none;
  animation: ${fade} 500ms ease both;
`;

// Curva monótona: suaviza sem inventar picos entre os meses
function smoothPath(points) {
  if (points.length === 0) return "";
  if (points.length === 1) return `M${points[0][0]},${points[0][1]}`;
  const slopes = points.slice(0, -1).map((p, i) => (points[i + 1][1] - p[1]) / (points[i + 1][0] - p[0]));
  const tangents = points.map((_, i) => {
    if (i === 0) return slopes[0];
    if (i === points.length - 1) return slopes[slopes.length - 1];
    const [a, b] = [slopes[i - 1], slopes[i]];
    return a * b <= 0 ? 0 : (2 * a * b) / (a + b);
  });
  let d = `M${points[0][0].toFixed(1)},${points[0][1].toFixed(1)}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const [x0, y0] = points[i];
    const [x1, y1] = points[i + 1];
    const third = (x1 - x0) / 3;
    d += ` C${(x0 + third).toFixed(1)},${(y0 + tangents[i] * third).toFixed(1)} ${(x1 - third).toFixed(1)},${(y1 - tangents[i + 1] * third).toFixed(1)} ${x1.toFixed(1)},${y1.toFixed(1)}`;
  }
  return d;
}

function niceDomain(values) {
  const low = Math.min(...values);
  const high = Math.max(...values);
  const range = high - low || high || 1;
  const rough = range / 4;
  const power = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((n) => n * power).find((s) => s >= rough) || rough;
  const min = Math.max(0, Math.floor(low / step) * step);
  const max = Math.ceil(high / step) * step;
  const ticks = [];
  for (let t = min; t <= max + step / 2; t += step) ticks.push(t);
  return { min, max: max > min ? max : min + step, ticks };
}

// Mantém dois rótulos na mesma altura sem que um cubra o outro
function apart(a, b, distance) {
  if (Math.abs(a - b) >= distance) return [a, b];
  const mid = (a + b) / 2;
  return a <= b ? [mid - distance / 2, mid + distance / 2] : [mid + distance / 2, mid - distance / 2];
}

export default function MonthlyChart({ months, selected, onSelect }) {
  const theme = useTheme();
  const stage = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [hover, setHover] = useState(null);
  const [touched, setTouched] = useState(false);

  useLayoutEffect(() => {
    const el = stage.current;
    if (!el) return undefined;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const count = months.length;
  const innerW = Math.max(1, size.w - PAD.left - PAD.right);
  const mainH = Math.max(1, size.h - PAD.top - PAD.bottom - LANE - LANE_GAP);
  const x = (i) => PAD.left + (count > 1 ? (i / (count - 1)) * innerW : innerW / 2);

  const domain = useMemo(
    () => niceDomain(months.flatMap((m) => [m.faturamento || 0, m.compras || 0])),
    [months],
  );
  const y = (v) => PAD.top + ((domain.max - v) / (domain.max - domain.min)) * mainH;

  const laneTop = PAD.top + mainH + LANE_GAP;
  const netHigh = Math.max(0, ...months.map((m) => m.net));
  const netLow = Math.min(0, ...months.map((m) => m.net));
  const netSpan = netHigh - netLow || 1;
  const zeroY = laneTop + (netHigh / netSpan) * LANE;
  const barHeight = (v) => Math.max(v === 0 ? 0 : 2, (Math.abs(v) / netSpan) * LANE);

  const paths = useMemo(() => {
    if (!size.w) return null;
    const rev = months.map((m, i) => [x(i), y(m.faturamento || 0)]);
    const pur = months.map((m, i) => [x(i), y(m.compras || 0)]);
    const revenue = smoothPath(rev);
    const purchases = smoothPath(pur);
    const back = smoothPath([...pur].reverse()).replace(/^M/, "L");
    const left = x(0);
    const right = x(count - 1);
    return {
      revenue,
      purchases,
      band: `${revenue} ${back} Z`,
      below: `${revenue} L${right},${PAD.top + mainH} L${left},${PAD.top + mainH} Z`,
      above: `${revenue} L${right},${PAD.top} L${left},${PAD.top} Z`,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months, size.w, size.h, domain.min, domain.max]);

  const indexAt = (e) => {
    const px = e.clientX - stage.current.getBoundingClientRect().left;
    if (count < 2) return 0;
    const i = Math.round(((px - PAD.left) / innerW) * (count - 1));
    return Math.max(0, Math.min(count - 1, i));
  };
  const selectedIndex = months.findIndex((m) => m.periodo === selected);

  const pick = (i) => {
    setTouched(true);
    if (months[i].missing) return;
    onSelect(months[i].periodo === selected ? null : months[i].periodo);
  };
  const onKey = (e) => {
    if (e.key === "Escape") { onSelect(null); return; }
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    setTouched(true);
    const direction = e.key === "ArrowRight" ? 1 : -1;
    let next = selectedIndex >= 0 ? selectedIndex + direction : (direction > 0 ? 0 : count - 1);
    while (months[next]?.missing) next += direction;
    if (months[next]) onSelect(months[next].periodo);
  };

  const ready = size.w > 0 && paths;
  // Rótulos de mês só onde cabem, sem encostar uns nos outros
  const every = Math.max(1, Math.ceil(46 / (innerW / Math.max(1, count - 1))));
  const line = tokens.separator(theme);
  const last = months[count - 1];
  const [endRevenue, endPurchases] = ready ? apart(y(last.faturamento || 0), y(last.compras || 0), 15) : [0, 0];
  const pickedMonth = selectedIndex >= 0 ? months[selectedIndex] : null;
  const showHover = hover !== null && hover !== selectedIndex;
  const hovered = showHover ? months[hover] : null;
  const flip = selectedIndex >= 0 && x(selectedIndex) > PAD.left + innerW * 0.8;
  const chipSide = flip ? { transform: "translate(calc(-100% - 12px), -50%)" } : { transform: "translate(12px, -50%)" };
  const [chipRevenue, chipPurchases] = ready && pickedMonth
    ? apart(y(pickedMonth.faturamento || 0), y(pickedMonth.compras || 0), 26)
    : [0, 0];

  return (
    <Stage
      ref={stage}
      role="slider"
      tabIndex={0}
      aria-label="Mês em destaque"
      aria-valuemin={0}
      aria-valuemax={Math.max(0, count - 1)}
      aria-valuenow={Math.max(0, selectedIndex)}
      aria-valuetext={pickedMonth ? formatMonth(pickedMonth.periodo) : "Período inteiro"}
      onClick={(e) => pick(indexAt(e))}
      onPointerMove={(e) => setHover(indexAt(e))}
      onPointerLeave={() => setHover(null)}
      onKeyDown={onKey}
    >
      {ready && (
        <>
          <svg aria-hidden="true">
            <defs>
              <linearGradient id="mc-rev" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={REVENUE} stopOpacity={0.4} />
                <stop offset="100%" stopColor={REVENUE} />
              </linearGradient>
              <linearGradient id="mc-pur" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={PURCHASES} stopOpacity={0.4} />
                <stop offset="100%" stopColor={PURCHASES} />
              </linearGradient>
              <clipPath id="mc-below"><path d={paths.below} /></clipPath>
              <clipPath id="mc-above"><path d={paths.above} /></clipPath>
            </defs>

            {domain.ticks.map((t) => <line key={t} x1={PAD.left} x2={PAD.left + innerW} y1={y(t)} y2={y(t)} stroke={line} strokeOpacity={0.55} />)}

            {count > 1 && (
              <>
                <g clipPath="url(#mc-below)"><path className="band" d={paths.band} fill={REVENUE} fillOpacity={0.09} /></g>
                <g clipPath="url(#mc-above)"><path className="band" d={paths.band} fill={PURCHASES} fillOpacity={0.16} /></g>
                <path className="curve enter" pathLength={1} d={paths.revenue} stroke="url(#mc-rev)" style={{ filter: `drop-shadow(0 0 5px ${REVENUE}55)` }} />
                <path className="curve enter" pathLength={1} d={paths.purchases} stroke="url(#mc-pur)" style={{ filter: `drop-shadow(0 0 5px ${PURCHASES}55)` }} />
              </>
            )}

            {/* Tributo líquido do mês, no mesmo eixo */}
            <line x1={PAD.left} x2={PAD.left + innerW} y1={zeroY} y2={zeroY} stroke={tokens.label3(theme)} strokeOpacity={0.6} />
            {months.map((m, i) => {
              const h = barHeight(m.net);
              const width = Math.min(20, Math.max(5, (innerW / Math.max(1, count)) * 0.46));
              return (
                <rect
                  key={m.periodo}
                  className="bar"
                  x={x(i) - width / 2}
                  y={m.net >= 0 ? zeroY - h : zeroY}
                  width={width}
                  height={h}
                  rx={2.5}
                  fill={m.net >= 0 ? PAY : CREDIT}
                  opacity={selectedIndex < 0 || selectedIndex === i ? 0.85 : hover === i ? 0.7 : 0.3}
                />
              );
            })}

            {showHover && (
              <>
                <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={laneTop + LANE} stroke={tokens.label3(theme)} strokeDasharray="2 4" />
                <circle cx={x(hover)} cy={y(hovered.faturamento || 0)} r={3} fill={REVENUE} />
                <circle cx={x(hover)} cy={y(hovered.compras || 0)} r={3} fill={PURCHASES} />
              </>
            )}

            {pickedMonth && (
              <>
                <line x1={x(selectedIndex)} x2={x(selectedIndex)} y1={PAD.top - 6} y2={laneTop + LANE + 4} stroke={tokens.label(theme)} strokeOpacity={0.5} />
                <circle cx={x(selectedIndex)} cy={y(pickedMonth.faturamento || 0)} r={4.5} fill={REVENUE} stroke={tokens.solid(theme)} strokeWidth={2} />
                <circle cx={x(selectedIndex)} cy={y(pickedMonth.compras || 0)} r={4.5} fill={PURCHASES} stroke={tokens.solid(theme)} strokeWidth={2} />
              </>
            )}

            {count === 1 && !pickedMonth && (
              <>
                <circle cx={x(0)} cy={y(last.faturamento || 0)} r={4.5} fill={REVENUE} />
                <circle cx={x(0)} cy={y(last.compras || 0)} r={4.5} fill={PURCHASES} />
              </>
            )}
          </svg>

          {domain.ticks.map((t) => (
            <Tick key={t} style={{ left: 0, width: PAD.left - 10, textAlign: "right", top: y(t) - 7 }}>{formatCompact(t).replace("R$", "").trim()}</Tick>
          ))}
          <Tick style={{ left: 0, width: PAD.left - 10, textAlign: "right", top: laneTop + 2, lineHeight: 1.25 }}>Tributo<br />líquido</Tick>
          {months.map((m, i) => (i % every === 0 || i === selectedIndex) && (
            <Tick key={m.periodo} className={i === selectedIndex ? "on" : ""} style={{ left: x(i), top: laneTop + LANE + 9, transform: "translateX(-50%)" }}>{formatMonth(m.periodo)}</Tick>
          ))}

          <EndLabel style={{ left: x(count - 1) + 10, top: endRevenue, color: REVENUE }}>Faturamento</EndLabel>
          <EndLabel style={{ left: x(count - 1) + 10, top: endPurchases, color: PURCHASES }}>Compras</EndLabel>

          {pickedMonth && (
            <>
              <Chip $color={REVENUE} style={{ left: x(selectedIndex), top: chipRevenue, ...chipSide }}>{formatCompact(pickedMonth.faturamento)}</Chip>
              <Chip $color={PURCHASES} style={{ left: x(selectedIndex), top: chipPurchases, ...chipSide }}>{formatCompact(pickedMonth.compras)}</Chip>
            </>
          )}

          {hovered && (
            <Glass style={{ left: Math.min(x(hover) + 12, size.w - 192) }}>
              <div className="p">{formatMonth(hovered.periodo)}{hovered.missing ? " · sem SPED" : ""}</div>
              <div className="r"><span><i style={{ background: REVENUE }} />Faturamento</span><b>{formatCompact(hovered.faturamento)}</b></div>
              <div className="r"><span><i style={{ background: PURCHASES }} />Compras</span><b>{formatCompact(hovered.compras)}</b></div>
              <div className="r"><span><i style={{ background: hovered.net >= 0 ? PAY : CREDIT }} />Tributo líquido</span><b>{formatCompact(hovered.net)}</b></div>
            </Glass>
          )}

          {!touched && !selected && hover === null && <Hint>Clique em um mês para ver o detalhe na aba inteira</Hint>}
        </>
      )}
    </Stage>
  );
}
